import React, { useCallback, useEffect, useMemo } from "react";
import { Typography, Card, Spin } from "antd";
import Chart from "react-apexcharts";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";
import { flattenMetrics } from "../../../../../../helper";

const { Text } = Typography;

interface FinancialAnatomyProps {
  selectedStock?: any;
  baseCurrency: string;
  metricsData?: any;
}

const BAR_LABELS = ["Revenue", "Gross", "Operating", "PBT", "PAT"];
const STAT_LABELS = [
  "GROSS MARGIN",
  "OPERATING MARGIN",
  "PBT MARGIN",
  "PAT MARGIN",
];

const INCLUDED_METRIC_NAMES = [
  "revenues",
  "gross_profits",
  "operating_profit",
  "profit_before_tax",
  "net_profits",
];

const formatLKR = (value: number) => {
  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
};

export const FinancialAnatomy: React.FC<FinancialAnatomyProps> = ({
  selectedStock,
  baseCurrency,
  metricsData,
}) => {
  const {
    execute: generateFinancialAnatomyGraph,
    loading: financialAnatomyLoading,
    response: financialAnatomyGraphData,
  } = useFetchApi("/api/dynamic-graph");

  // Filter + reorder the shared flattened metrics to match INCLUDED_METRIC_NAMES order
  const selectedMetrics = useMemo(() => {
    if (!metricsData) return [];
    const flattened = flattenMetrics(metricsData);
    return INCLUDED_METRIC_NAMES.map((name) =>
      flattened.find((m: any) => m.name?.trim() === name),
    ).filter(Boolean);
  }, [metricsData]);

  const fetchData = useCallback(() => {
    if (!selectedStock || selectedMetrics.length === 0) return;

    generateFinancialAnatomyGraph({
      selectedStocks: [selectedStock],
      filter: "annually",
      baseCurrency,
      selectedMetrics,
      period: "3",
    });
  }, [selectedStock, baseCurrency, selectedMetrics]);

  useEffect(() => {
    fetchData();
  }, [selectedStock, baseCurrency, selectedMetrics]);

  const { barValues, stats } = useMemo(() => {
    const series = financialAnatomyGraphData?.series;

    if (!series || series.length < 5) {
      return { barValues: [], stats: [] };
    }

    const latest = series.map((s: any) => {
      const last = s.data?.[s.data.length - 1];
      return last ? parseFloat(last) : 0;
    });

    const [revenue, gross, operating, pbt, pat] = latest;

    const computeStat = (value: number) => ({
      percent: revenue ? (value / revenue) * 100 : 0,
      value,
    });

    return {
      barValues: latest,
      stats: [
        computeStat(gross),
        computeStat(operating),
        computeStat(pbt),
        computeStat(pat),
      ],
    };
  }, [financialAnatomyGraphData]);

  const chartOptions = {
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "45%",
        borderRadius: 4,
        borderRadiusApplication: "end" as const,
      },
    },
    dataLabels: { enabled: false },
    // Was a hardcoded teal literal regardless of theme — now tracks
    // --vi-accent so the bars actually switch color with the active
    // palette (blue/teal) and dark/light mode, same as every other
    // themed chart in the app. ApexCharts resolves this CSS var string
    // at paint time the same way a stylesheet color would.
    colors: ["var(--vi-accent)"],
    grid: {
      borderColor: "var(--vi-card-border)",
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: BAR_LABELS,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: "var(--vi-text-tertiary)", fontSize: "12px" },
      },
    },
    yaxis: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${baseCurrency} ${formatLKR(val)}`,
      },
    },
  };

  const chartSeries = [
    {
      name: "Value",
      data: barValues,
    },
  ];

  return (
    <Card className="vi-glass-card w-full shadow-sm rounded-xl">
      <div className="flex justify-between items-start mb-1">
        <div>
          <Text strong className="text-sm text-gray-800 block">
            Financial Anatomy
          </Text>
          <Text className="text-xs text-gray-400 uppercase tracking-wide">
            Profitability Bridge
          </Text>
        </div>

        {barValues.length > 0 && (
          <div className="flex flex-col items-end">
            <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
              Total Revenue
            </Text>
            <Text className="text-lg font-semibold text-gray-800">
              {baseCurrency} {formatLKR(barValues[0])}
            </Text>
          </div>
        )}
      </div>

      {financialAnatomyLoading ? (
        <div className="h-48 flex items-center justify-center">
          <Spin />
        </div>
      ) : (
        <>
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={180}
          />

          <div className="grid grid-cols-4 gap-4 mt-4">
            {STAT_LABELS.map((label, i) => {
              const stat = stats[i];
              if (!stat) return null;

              return (
                <div key={label} className="flex flex-col">
                  <Text className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
                    {label}
                  </Text>
                  <Text
                    className="text-lg font-semibold"
                    style={{ color: "var(--vi-accent)" }}
                  >
                    {stat.percent >= 0 ? "+" : ""}
                    {stat.percent.toFixed(1)}%
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {baseCurrency} {formatLKR(stat.value)}
                  </Text>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};
